/**
 * O que o sistema sabe sobre um tipo de componente — em um lugar só.
 *
 * No modelo antigo, acrescentar um tipo exigia editar 7 ou mais pontos: a união de
 * strings, a interface de props, `createDefaultProps`, três conversores de webhook,
 * o `ngSwitch` do canvas em quatro níveis de aninhamento, a seção do inspector e o
 * serviço de validação. Aqui é uma linha no `NodePropsMap` e um descriptor.
 */
import { LayoutSpec } from '../model/layout';
import { NodePropsMap, NodeType, ScreenNode } from '../model/node-props';
import { PropertyGroup, PropertyLevel } from './property-schema';

/** Categoria na paleta. */
export type ComponentCategory =
  | 'layout'
  | 'input'
  | 'selection'
  | 'data'
  | 'action'
  | 'display';

/** Como um container dispõe os filhos. */
export type ContainerLayoutMode = 'grid' | 'grid-free' | 'row' | 'stack' | 'tabs';

/** Regras de aninhamento de um container. */
export interface ContainerRules {
  readonly layoutMode: ContainerLayoutMode;
  /** Tipos aceitos como filho. */
  readonly accepts: readonly NodeType[];
  /** Mínimo de filhos; abaixo disso a validação da tela reclama. */
  readonly minChildren?: number;
  /** Máximo de filhos. */
  readonly maxChildren?: number;
}

/** Contexto passado na criação de um nó novo. */
export interface CreateContext {
  /** Id já reservado para o nó. */
  readonly id: string;
  /** Tipo do container que vai receber, quando houver. */
  readonly parentType?: NodeType;
  /** Ordem dentro do container. */
  readonly order: number;
}

/** O que `createDefault` devolve. */
export interface CreateResult<K extends NodeType> {
  readonly props: NodePropsMap[K];
  readonly layout?: Partial<LayoutSpec>;
  /** Filhos criados junto. Ex.: abas nascem com uma aba dentro. */
  readonly children?: readonly ScreenNode[];
}

/** Participação do nó no formulário. Só campos que guardam valor têm isto. */
export interface FormContribution<K extends NodeType> {
  /** Valor inicial do controle. */
  readonly defaultValue: (node: ScreenNode<K>) => unknown;
  /** Se o campo é obrigatório de forma estática. */
  readonly isRequired?: (node: ScreenNode<K>) => boolean;

  /**
   * QUE FORMA de controle este tipo precisa.
   *
   * `'control'` é o padrão e cobre todo campo de valor único. `'array'` existe
   * para os tipos que guardam uma LISTA de linhas editáveis — tabela e tabela
   * simples — porque o widget deles faz `[formArrayName]`, e o Angular estoura
   * se encontrar um `FormControl` ali. Não é preferência de estilo: com a forma
   * errada, a tela inteira deixa de renderizar.
   */
  readonly control?: 'control' | 'array';
}

export interface ComponentDescriptor<K extends NodeType = NodeType> {
  readonly type: K;

  /** Chave de tradução do nome na paleta e no inspector. */
  readonly labelKey: string;
  /** Chave de tradução da descrição curta na paleta. */
  readonly descriptionKey?: string;
  /** Ícone do NG-Zorro. */
  readonly icon: string;
  readonly category: ComponentCategory;

  /**
   * Nível de exposição do TIPO. `advanced` some da paleta no modo simples.
   * É um dos três eixos do público duplo — os outros dois são o nível do grupo de
   * propriedades e as capacidades do editor.
   */
  readonly level: PropertyLevel;

  /** Estado inicial ao arrastar da paleta. Substitui `createDefaultProps`. */
  createDefault(context: CreateContext): CreateResult<K>;

  /**
   * Regras de container. Presente só em quem aceita filhos.
   * Substitui o `BuilderValidationService`, que tinha a hierarquia escrita à mão.
   */
  readonly container?: ContainerRules;

  /**
   * Onde este tipo pode ser solto. `'any'` = qualquer container que o aceite.
   * É o espelho de `container.accepts`, avaliado do lado do filho.
   */
  readonly allowedParents: readonly NodeType[] | 'any';

  /** Propriedades exibidas no inspector, em grupos. */
  readonly inspector: readonly PropertyGroup[];

  /** Contribuição para o formulário. Ausente em container e em elemento estático. */
  readonly form?: FormContribution<K>;

  /**
   * Componente de render. Preenchido na Fase 3, quando o renderer existir —
   * mantido opcional aqui para o registry seguir sendo TypeScript puro, testável
   * sem TestBed e sem depender do Angular.
   */
  readonly render?: unknown;
}

/**
 * Ajuda a declarar um descriptor com o tipo amarrado.
 * Sem isto, `createDefault` devolveria a união de todas as props e o TypeScript
 * não conseguiria conferir se as props batem com o tipo declarado.
 */
export function defineDescriptor<K extends NodeType>(
  descriptor: ComponentDescriptor<K>
): ComponentDescriptor<K> {
  return descriptor;
}
